/* eslint-disable @gitlab/require-i18n-strings */
import isObject from 'lodash-es/isObject';
import { captureExceptionForDuoChat } from '../../observability/sentry_utils';

/**
 * @typedef {Object} SlashCommand
 * @property {string} name - The command, leading slash included, e.g. `/compact`.
 * @property {string} description - Rendered under the name in the suggestion menu.
 * @property {boolean} [shouldSubmit] - `true` sends the prompt as soon as the command
 *   is chosen, instead of leaving it in the composer.
 * @property {boolean} [startOnly] - `true` offers the command only at the start of the
 *   prompt, for commands that act on the whole conversation.
 */

/**
 * @typedef {Object} SlashCommandsProvider
 * @property {(dependencies: Object) => Promise<SlashCommand[]>|SlashCommand[]} getCommands -
 *   Awaited, so a provider with nothing to fetch can answer synchronously. Asked for
 *   its commands the first time the user types a `/`, and again whenever the chat
 *   context changes. Receives `{ apollo, duoChatContext }`; both are best-effort, so
 *   guard them: `duoChatContext` is `{}` in mounts that have no state manager above
 *   them, and `apollo` is unusable outside a component tree with an Apollo provider.
 */

const report = (message) => captureExceptionForDuoChat(new Error(message));

const providersOf = (plugins) =>
  plugins.flatMap(({ name, slashCommands: providers = [] }) =>
    providers.map((provider) => ({ plugin: name, provider })),
  );

async function commandsFrom({ plugin, provider }, dependencies) {
  try {
    const commands = await provider.getCommands(dependencies);

    if (!Array.isArray(commands)) {
      report(`Duo Chat plugin "${plugin}" did not return an array of slash commands`);
      return [];
    }

    return commands.filter((command) => {
      // The menu lowercases `name` while filtering, so a nameless command throws
      // inside a computed rather than merely failing to render.
      if (typeof command?.name === 'string') return true;

      report(`Duo Chat plugin "${plugin}" contributed a slash command without a name`);
      return false;
    });
  } catch (error) {
    // Reported and dropped: one provider failing must not empty the whole menu.
    captureExceptionForDuoChat(error);
    return [];
  }
}

// First contribution wins, matching the precedence rule the other capabilities follow.
// Left to itself a collision would surface as a Vue duplicate-key warning, because the
// menu keys its options on the command name.
function dedupeByName(commands) {
  const seen = new Set();

  return commands.filter(({ name }) => {
    if (!seen.has(name)) {
      seen.add(name);
      return true;
    }

    report(`Duplicate Duo Chat slash command \`${name}\` dropped`);
    return false;
  });
}

export const slashCommands = {
  key: 'slashCommands',

  /**
   * Validates if a slash commands provider is valid.
   *
   * @param {unknown} provider
   * @returns {string[]} One message per contract violation; empty when valid.
   */
  validate(provider) {
    if (!isObject(provider)) {
      return ['must be an object'];
    }

    return typeof provider.getCommands === 'function' ? [] : ['`getCommands` must be a function'];
  },

  /**
   * Asks every plugin for the commands it offers and flattens the answers into the
   * list the suggestion menu renders. One pass per call: callers that ask repeatedly
   * are expected to hold the result themselves.
   *
   * @param {import('../plugin_registry').DuoChatPlugin[]} plugins
   * @param {Object} [dependencies] - Handed to each provider unread. `apollo` is the
   *   caller's vue-apollo wrapper; `duoChatContext` carries the ids the chat is
   *   pointed at.
   * @returns {Promise<SlashCommand[]>}
   */
  async resolve(plugins, dependencies = {}) {
    const perProvider = await Promise.all(
      providersOf(plugins).map((entry) => commandsFrom(entry, dependencies)),
    );

    return dedupeByName(perProvider.flat());
  },
};
